import React, { FC } from 'react';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import { Controller } from 'react-hook-form';
import styled from 'styled-components';
import _ from 'lodash';

type Props = {
  register: any;
  errors: any;
  control: any;
  dropdownItems: any[];
  name: string;
  label?: string;
  externalValue?: any;
  setValue?: Function;
  trigger?: Function;
  width?: number;
  required?: boolean;
};

const Dropdown: FC<Props> = ({
  register,
  control,
  errors,
  dropdownItems,
  name,
  label,
  width,
  required,
}) => {
  const hasError = errors[name] !== undefined;
  const displayLabel = label || _.upperFirst(name);

  return (
    <ClientTypeWrapper width={width}>
      <FieldLabel $error={hasError}>
        {displayLabel}
        {required && <Asterisk>*</Asterisk>}
      </FieldLabel>

      <Controller
        as={
          <StyledSelect
            variant="outlined"
            $error={hasError}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <Placeholder>Select {displayLabel.toLowerCase()}</Placeholder>
            </MenuItem>
            {dropdownItems.map((item, index) => (
              <MenuItem value={item.id} key={index}>
                {item.title}
              </MenuItem>
            ))}
          </StyledSelect>
        }
        control={control}
        inputRef={register}
        name={name}
        defaultValue=""
        error={hasError}
      />

      {hasError && (
        <ErrorMessage>{errors[name].message}</ErrorMessage>
      )}
    </ClientTypeWrapper>
  );
};

export default Dropdown;

const ClientTypeWrapper = styled.div<{ width?: number }>`
  display: flex;
  flex-direction: column;
  width: ${({ width }) => (width ? `${width}%` : '100%')};
  font-family: inherit;
`;

const FieldLabel = styled.label<{ $error: boolean }>`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ $error }) =>
    $error
      ? 'var(--danger-500, #dc2626)'
      : 'var(--mainText)'};
  margin-bottom: 6px;
`;

const Asterisk = styled.span`
  color: var(--danger-500, #dc2626);
  margin-left: 2px;
`;

const StyledSelect = styled(Select)<{ $error: boolean }>`
  && {
    width: 100%;
    height: 40px;
    background: var(--neutral-0);
    font-family: inherit;
    font-size: 0.875rem;
    color: var(--mainText);

    .MuiSelect-root,
    .MuiSelect-select {
      padding: 10px 32px 10px 12px !important;
      min-height: 20px;
      box-sizing: border-box;
      border-radius: 8px;
    }

    .MuiOutlinedInput-notchedOutline {
      border: 1px solid
        ${({ $error }) =>
          $error
            ? 'var(--danger-500, #dc2626)'
            : 'var(--border)'} !important;
      border-radius: 8px !important;
      top: 0 !important;
    }

    .MuiOutlinedInput-notchedOutline legend {
      display: none !important;
    }

    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: var(--neutral-400) !important;
    }

    &.Mui-focused .MuiOutlinedInput-notchedOutline {
      border: 1px solid var(--primary-600) !important;
      box-shadow: var(--shadow-focus);
    }
  }
`;

const Placeholder = styled.span`
  color: var(--neutral-400);
`;

const ErrorMessage = styled.div`
  font-size: 0.75rem;
  color: var(--danger-500, #dc2626);
  margin-top: 4px;
`;
